import React from 'react';
import { Box, Paper, Typography, Grid } from '@mui/material';
import { useTranslation } from '../../hooks/useTranslation';

/**
 * Component for displaying ZATCA QR code section with Arabic and English descriptions
 * 
 * @param {Object} props - Component props
 * @param {string} props.qrCodeUrl - URL for the QR code image
 * @param {string} props.invoiceNumber - Invoice number for QR code generation
 */
const ZatcaQrSection = ({ 
  qrCodeUrl = 'https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=ZATCA-DEMO-INVOICE-123456',
  invoiceNumber
}) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              borderRadius: 0,
              border: '1px solid rgba(0, 0, 0, 0.12)',
              boxShadow: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            {/* QR Code Section */}
            <Box sx={{ 
              display: 'flex',
              alignItems: 'flex-start',
              gap: 3
            }}>
              <Box sx={{ 
                p: 2, 
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: 0,
                bgcolor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                minWidth: 200
              }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.875rem', alignSelf: 'flex-start' }}>
                  {t('Sales.ZATCA_QR_Code')}
                </Typography>
                <Box 
                  component="img" 
                  src={qrCodeUrl}
                  alt="ZATCA QR Code"
                  sx={{ 
                    width: 150, 
                    height: 150,
                    objectFit: 'contain',
                    filter: 'blur(4px)',
                    opacity: 0.7,
                    transition: 'filter 0.3s, opacity 0.3s'
                  }}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                {/* Arabic Section */}
                <Box sx={{ 
                  textAlign: 'right',
                  direction: 'rtl',
                  fontFamily: 'Arial, sans-serif',
                  mb: 2
                }}>
                  <Typography variant="body1" sx={{ fontSize: '0.875rem', mb: 1, fontWeight: 500 }}>
                    فاتورة إلكترونية معتمدة من هيئة الزكاة والضريبة والجمارك
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1 }}>
                    رمز الاستجابة السريعة (QR) يحتوي على تفاصيل الفاتورة الضريبية المبسطة
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    يمكنك مسح رمز الاستجابة السريعة للتحقق من صحة الفاتورة وتفاصيلها
                  </Typography>
                </Box>

                {/* English Section */}
                <Box sx={{ 
                  textAlign: 'left',
                  borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                  pt: 2
                }}>
                  <Typography variant="body1" sx={{ fontSize: '0.875rem', mb: 1, fontWeight: 500 }}>
                    ZATCA E-Invoice Compliant
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1 }}>
                    The QR code contains the details of the simplified tax invoice or standard tax invoice
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Please Scan the QR code to verify the invoice details and authenticity
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* ZATCA Logo and Approval */}
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 1,
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              pt: 2
            }}>
              <Box 
                component="img" 
                src="/images/zatca-logo.svg" 
                alt="ZATCA Logo"
                sx={{ 
                  height: 30,
                  width: 100,
                  objectFit: 'contain'
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {t('Sales.ZATCA_Approved_E_Invoice_System')}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ZatcaQrSection;
